'use client'

/**
 * MediaField Component
 *
 * Handles the complete media upload flow with immediate preview and accurate progress tracking.
 *
 * Key Features:
 * 1. Immediate Preview: Uses URL.createObjectURL() to show selected files instantly
 * 2. Accurate Upload Progress: Uses XMLHttpRequest for precise upload progress tracking
 * 3. State Management: Handles upload, preview, and processing states
 *
 * Upload Flow:
 * 1. File Selection -> Immediate preview using URL.createObjectURL()
 * 2. Upload Start -> XMLHttpRequest tracks progress
 * 3. Upload Complete -> Server processes file
 * 4. Processing Complete -> Final media item displayed
 */

import { getMediaConfig } from '@/config/media-upload'
import { useMediaUpload } from '@/hooks/use-media-upload-context'
import {
  IMediaItem,
  IMediaPreview,
  IUploadProgress,
  MediaError,
  MediaStatus,
  UploadStatus
} from '@/lib/types/media/core'
import { createStoreMetadata } from '@/lib/types/media/metadata'
import { generateUUID } from '@/lib/utils/crypto'
import { showToast } from '@/lib/utils/toast'
import { MediaPlacement, MediaType } from '@prisma/client'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FileRejection } from 'react-dropzone'
import { MediaDropzone } from '../core/MediaDropzone'
import { MediaEditor } from '../editor/MediaEditor'
import { UploadPreview } from '../upload/UploadPreview'

export interface MediaFieldProps {
  placement: MediaPlacement
  ownerId: string
  value?: IMediaItem | null
  onChange?: (media: IMediaItem | null) => void
  onProgress?: (progress: IUploadProgress) => void
  aspectRatio?: 'square' | 'video' | 'auto'
  maxFiles?: number
  className?: string
  id?: string // Optional ID for tracking upload state
}

export function MediaField({
  placement,
  ownerId,
  value,
  onChange,
  onProgress,
  aspectRatio = 'video',
  maxFiles = 1,
  className,
  id = generateUUID()
}: MediaFieldProps) {
  const [preview, setPreview] = useState<IMediaPreview | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const config = getMediaConfig(placement)
  const { updateUploadState: contextUpdateUploadState, clearUploadState: contextClearUploadState } = useMediaUpload()

  // Track object URLs for cleanup to prevent memory leaks
  const objectUrlsRef = useRef<Set<string>>(new Set())

  // Methods to update and manage upload state
  const updateUploadState = useCallback(
    (mediaId: string, status: UploadStatus) => {
      // Update upload state in the context
      contextUpdateUploadState(mediaId, status)
    },
    [contextUpdateUploadState]
  )

  const clearUploadState = useCallback(
    (mediaId: string) => {
      // Clear upload state from the context
      contextClearUploadState(mediaId)
    },
    [contextClearUploadState]
  )

  // Cleanup resources on unmount
  useEffect(() => {
    return () => {
      // Clear any upload state associated with this field
      clearUploadState(id)

      // Clean up preview if it exists
      if (preview?.previewUrl) {
        try {
          URL.revokeObjectURL(preview.previewUrl)
        } catch (error) {
          console.warn('Failed to revoke preview URL:', preview.previewUrl, error)
        }
      }

      // Clean up all tracked object URLs to prevent memory leaks
      objectUrlsRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url)
        } catch (error) {
          console.warn('Failed to revoke object URL:', url, error)
        }
      })
      objectUrlsRef.current.clear()
    }
  }, [id, clearUploadState, preview])

  /**
   * Handles the file upload process using mediaService.upload
   * which is more reliable across browsers.
   */
  const handleUpload = useCallback(
    async (file: File, preview: IMediaPreview) => {
      try {
        setIsUploading(true)
        setUploadProgress(0)

        // Update upload state to indicate upload has started
        updateUploadState(id, {
          status: 'uploading',
          progress: 0
        })

        // Log upload parameters for debugging
        console.log('🚀 Upload request params:', {
          placement,
          uploadId: id,
          userId: ownerId,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type
        })

        // Create complete metadata with all required fields
        const completeMetadata = createStoreMetadata(ownerId, {
          placement,
          mimeType: file.type,
          size: file.size,
          alt: file.name,
          caption: file.name,
          originalName: file.name,
          orderIndex: 0,
          // Include custom path for certain placements if needed
          ...(placement === MediaPlacement.ARTICLE_MEDIA && {
            customPath: `articles/${ownerId}`
          })
        })

        // We'll use XMLHttpRequest which has better cross-browser support for uploads
        return new Promise<IMediaItem>((resolve, reject) => {
          // Create the XHR request
          const xhr = new XMLHttpRequest()

          // Track upload progress
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const percentage = Math.round((event.loaded / event.total) * 100)
              setUploadProgress(percentage)

              // Update upload state in context
              updateUploadState(id, {
                status: percentage < 100 ? 'uploading' : 'processing',
                progress: percentage
              })

              // Report progress to parent
              onProgress?.({
                progress: percentage,
                percentage,
                status: percentage < 100 ? 'uploading' : 'processing',
                bytesUploaded: event.loaded,
                bytesTotal: event.total
              })

              console.log(`Upload progress: ${percentage}%`)
            }
          })

          // Handle successful completion
          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const result = JSON.parse(xhr.responseText)

                if (result.success && result.media) {
                  console.log('Upload successful:', result.media)

                  // Create complete media object
                  const completeMedia: IMediaItem = {
                    ...result.media,
                    // Set default values for any potentially missing fields
                    type: result.media.type || MediaType.IMAGE,
                    mimeType: result.media.mimeType || file.type || 'application/octet-stream',
                    size: result.media.size || file.size || 0,
                    metadata: {
                      ...(result.media.metadata || {}),
                      placement,
                      ownerId
                    }
                  }

                  // Update upload state to indicate completion
                  updateUploadState(id, {
                    status: 'complete',
                    progress: 100
                  })

                  // Clear preview and set the final media
                  setPreview(null)
                  onChange?.(completeMedia)
                  onProgress?.({
                    progress: 100,
                    percentage: 100,
                    status: 'complete',
                    error: null
                  })

                  showToast({ title: 'Upload complete', color: 'success', timeout: 3000 })
                  resolve(completeMedia)
                } else {
                  const errorMsg = result.error || 'Upload failed'
                  console.error('Upload result error:', errorMsg)
                  reject(new Error(errorMsg))
                }
              } catch (parseError) {
                console.error('Error parsing response:', parseError)
                reject(new Error('Failed to parse server response'))
              }
            } else {
              // Handle HTTP errors
              console.error('HTTP error during upload:', xhr.status, xhr.statusText)
              let errorMessage = `HTTP error ${xhr.status}`

              try {
                const errorResult = JSON.parse(xhr.responseText)
                errorMessage = errorResult.error || errorMessage
              } catch (e) {
                // Use default error message if response isn't valid JSON
              }

              reject(new Error(errorMessage))
            }
          })

          // Handle network errors
          xhr.addEventListener('error', () => {
            console.error('Network error during upload')
            reject(new Error('Network error during upload'))
          })

          // Handle timeouts
          xhr.addEventListener('timeout', () => {
            console.error('Upload timed out')
            reject(new Error('Upload timed out'))
          })

          // Handle abortion
          xhr.addEventListener('abort', () => {
            console.warn('Upload aborted')
            reject(new Error('Upload aborted'))
          })

          // Set timeout to 30 seconds
          xhr.timeout = 30000

          // Open the request
          xhr.open('POST', '/api/upload', true)

          // Prepare form data
          const formData = new FormData()
          formData.append('file', file)
          formData.append('placement', placement)
          formData.append('uploadId', id)
          formData.append('userId', ownerId)

          if (placement === MediaPlacement.ARTICLE_MEDIA) {
            formData.append('customPath', `articles/${ownerId}`)
          }

          // Log the request before sending
          console.log('📤 Sending XHR upload request to /api/upload:', {
            formDataKeys: [...formData.keys()],
            placement,
            id
          })

          // Send the request
          xhr.send(formData)
        })
          .then((media) => {
            // This will be called when the Promise resolves successfully
            console.log('✅ Upload completed successfully:', {
              mediaId: media.id,
              url: media.url
            })
            return media
          })
          .catch((error) => {
            // This handles any errors in the Promise chain
            console.error('Upload failed:', error)

            // Create fallback with object URL if this is a network issue
            if (
              error.message.includes('Network') ||
              error.message.includes('timeout') ||
              error.message.includes('abort')
            ) {
              console.warn('Using local fallback due to network issues')

              // Create an object URL for the file
              const objectUrl = URL.createObjectURL(file)
              // Track this URL for cleanup
              objectUrlsRef.current.add(objectUrl)

              // Generate a temporary ID
              const tempId = `temp-${Date.now()}-${file.name.replace(/\W+/g, '-')}`

              // Get dimensions for images
              let width = null
              let height = null

              if (file.type.startsWith('image/')) {
                // We'll measure the image asynchronously, but return the media immediately
                const img = new Image()
                img.onload = () => {
                  width = img.width
                  height = img.height
                  // Clean up the temporary URL after measuring
                  URL.revokeObjectURL(img.src)
                }
                img.onerror = () => {
                  // Clean up the temporary URL even on error
                  URL.revokeObjectURL(img.src)
                }
                img.src = objectUrl
              }

              // Create a temporary media item
              const tempMedia: IMediaItem = {
                id: tempId,
                url: objectUrl,
                type: file.type.startsWith('video/') ? MediaType.VIDEO : MediaType.IMAGE,
                placement,
                mimeType: file.type,
                size: file.size,
                width,
                height,
                altText: file.name,
                caption: file.name,
                aspectRatio: null,
                dpi: null,
                createdAt: new Date(),
                updatedAt: new Date(),
                status: MediaStatus.PENDING,
                version: 1,
                processingComplete: false,
                isDeleted: false,
                metadata: {
                  ...completeMetadata,
                  needsServerUpload: true,
                  originalFile: file.name,
                  fileSize: file.size,
                  mimeType: file.type
                }
              }

              // Update upload state for offline mode
              updateUploadState(id, {
                status: 'error',
                progress: 100,
                error: 'Network issue - using local storage temporarily'
              })

              console.log('Created temporary media item:', {
                id: tempId,
                type: tempMedia.type
              })

              // Clear preview and set the temporary media
              setPreview(null)
              onChange?.(tempMedia)
              onProgress?.({
                progress: 100,
                percentage: 100,
                status: 'complete',
                error: null
              })

              showToast({
                title: 'Using temporary local storage while network is unavailable',
                color: 'warning',
                timeout: 5000
              })
              return tempMedia
            }

            // Not a network error, so show the error and update the preview
            const mediaError = new MediaError('UPLOAD_ERROR', error.message)

            setPreview((prev) => {
              if (!prev) return null
              return {
                ...prev,
                error: mediaError
              }
            })

            onProgress?.({
              progress: 0,
              percentage: 0,
              status: 'error',
              error: mediaError
            })

            showToast({ title: error.message || 'Upload failed', color: 'danger', timeout: 5000 })
            throw error
          })
      } catch (error) {
        console.error('Upload error details:', error)

        // Provide a more specific toast message for common errors
        let errorMessage = 'Upload failed'

        if (error instanceof Error) {
          if (error.message.includes('Network') || error.message.includes('connectivity')) {
            errorMessage = 'Network error: Check your internet connection'
          } else if (error.message.includes('Unauthorized')) {
            errorMessage = 'Authentication error: Please log in again'
          } else if (error.message.includes('timeout') || error.message.includes('timed out')) {
            errorMessage = 'Upload timed out: File may be too large'
          } else {
            errorMessage = error.message
          }
        }

        showToast({ title: errorMessage, color: 'danger', timeout: 5000 })

        const mediaError =
          error instanceof MediaError
            ? error
            : new MediaError('UPLOAD_ERROR', error instanceof Error ? error.message : 'Upload failed')

        setPreview((prev) => {
          if (!prev) return null
          return {
            ...prev,
            error: mediaError
          }
        })

        onProgress?.({
          progress: 0,
          percentage: 0,
          status: 'error',
          error: mediaError
        })

        updateUploadState(id, {
          status: 'error',
          progress: 0,
          error: mediaError.message
        })
      } finally {
        setIsUploading(false)
      }
    },
    [id, ownerId, placement, onChange, onProgress, updateUploadState]
  )

  /**
   * Handles file drop/selection with immediate preview.
   * Uses URL.createObjectURL() through mediaService.createPreview for instant preview
   * before upload begins.
   *
   * Benefits of URL.createObjectURL():
   * - Instant preview without waiting for upload
   * - Memory efficient (browser manages the object URL)
   * - Cleaned up automatically when component unmounts
   */
  const handleDrop = useCallback(
    async (files: File[]) => {
      const file = files[0]
      if (!file) return

      try {
        // Create preview immediately using URL.createObjectURL
        const previewUrl = URL.createObjectURL(file)
        const preview: IMediaPreview = {
          previewId: generateUUID(),
          previewUrl,
          file,
          uploadProgress: 0,
          processingStatus: 'pending',
          type: file.type.startsWith('image') ? MediaType.IMAGE : MediaType.VIDEO,
          placement,
          mimeType: file.type,
          size: file.size,
          metadata: {
            placement,
            ownerId,
            alt: null,
            caption: null,
            orderIndex: null
          }
        }
        // Track URL for cleanup
        objectUrlsRef.current.add(previewUrl)
        setPreview(preview)

        // Update upload state to indicate preview creation
        updateUploadState(id, {
          status: 'uploading',
          progress: 0
        })

        // Start upload immediately while preview is showing
        handleUpload(file, preview)
      } catch (error) {
        showToast({ title: 'Failed to create preview', color: 'danger', timeout: 5000 })
        console.error('Preview error:', error)

        // Update global upload state to reflect the error
        updateUploadState(id, {
          status: 'error',
          progress: 0,
          error: error instanceof Error ? error.message : 'Preview failed'
        })
      }
    },
    [ownerId, placement, handleUpload, id, updateUploadState]
  )

  const handleDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      const errors = rejections.map((rejection) => {
        const error = rejection.errors[0]
        if (error.code === 'file-too-large') {
          return `File is too large. Maximum size is ${config.maxSize} bytes.`
        }
        if (error.code === 'file-invalid-type') {
          return `Invalid file type. Accepted types: ${Object.keys(config.acceptedTypes).join(', ')}`
        }
        return error.message
      })

      showToast({ title: 'File validation failed: ' + errors.join(', '), color: 'danger', timeout: 5000 })

      // Update global upload state to reflect validation error
      updateUploadState(id, {
        status: 'error',
        progress: 0,
        error: errors.join(', ')
      })
    },
    [config, id, updateUploadState]
  )

  /**
   * Cleanup function to revoke object URLs and reset state
   * Important: Always revoke object URLs to prevent memory leaks
   */
  const handleCleanup = useCallback(() => {
    if (preview?.previewUrl) {
      try {
        URL.revokeObjectURL(preview.previewUrl)
        objectUrlsRef.current.delete(preview.previewUrl)
      } catch (error) {
        console.warn('Failed to revoke preview URL during cleanup:', preview.previewUrl, error)
      }
      setPreview(null)
    }

    // Clean up all tracked object URLs
    objectUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url)
      } catch (error) {
        console.warn('Failed to revoke object URL during cleanup:', url, error)
      }
    })
    objectUrlsRef.current.clear()

    // Clear any upload state associated with this field
    clearUploadState(id)
  }, [preview, id, clearUploadState])

  const handleCancel = useCallback(async () => {
    if (isUploading) {
      // TODO: Implement upload cancellation
      updateUploadState(id, {
        status: 'error',
        progress: 0,
        error: 'Upload cancelled by user'
      })
      handleCleanup()
    }
  }, [isUploading, handleCleanup, updateUploadState, id])

  return (
    <div className={className}>
      {value ? (
        <MediaEditor media={value} onSave={onChange} aspectRatio={aspectRatio} />
      ) : preview ? (
        <UploadPreview
          preview={preview}
          onCancel={handleCancel}
          aspectRatio={aspectRatio}
          isUploading={isUploading}
          progress={uploadProgress}
        />
      ) : (
        <MediaDropzone
          onDrop={handleDrop}
          onDropRejected={handleDropRejected}
          acceptedTypes={config.acceptedTypes}
          maxSize={config.maxSize}
          multiple={maxFiles > 1}
        />
      )}
    </div>
  )
}
